<script>
  import { onMount } from 'svelte';
  import { showAlert, user } from '../lib/stores.js';
  import { authFetch, markVoucherAsUnused, downloadVoucherWithQR } from '../lib/api.js';
  
  let loading = true;
  let error = null;
  let titles = [];
  let categories = [];
  let vouchers = [];
  let selectedTitleId = '0';
  let selectedCategoryId = '';
  let voucherStatus = 'all';
  let page = 1;
  let totalPages = 0;
  let currentPage = 1;
  let total = 0;
  let searchQuery = '';
  let showCreateTitleModal = false;
  let showCreateCategoryModal = false;
  let newTitleName = '';
  let newCategory = {
    titleId: '',
    nominal: '',
    amount: '',
    codePrefix: '',
    image: null,
    posX: 50,
    posY: 50,
    qrSize: 100,
    showCodeText: true
  };
  let imagePreview = null;
  let titleToDelete = null;
  let categoryToDelete = null;
  
  // Load titles
  async function fetchTitles() {
    try {
      loading = true;
      error = null;
      
      const response = await authFetch('/vouchers/titles');
      
      if (response.success) {
        titles = response.titles;
        
        // Auto-select first title if available
        if (titles.length > 0 && selectedTitleId === '0') {
          selectedTitleId = titles[0]._id;
          await fetchCategories();
        } else {
          // If no titles, set loading to false
          loading = false;
        }
      } else {
        throw new Error(response.message || 'Failed to load titles');
      }
    } catch (err) {
      console.error('Error loading titles:', err);
      error = 'Failed to load voucher titles. Please try again.';
      showAlert('error', error);
      loading = false;
    }
  }
  
  // Load categories based on selected title
  async function fetchCategories() {
    try {
      loading = true;
      
      const response = await authFetch(`/vouchers/categories/${selectedTitleId}`);
      
      if (response.success) {
        categories = response.categories;
        
        // Auto-select first category if available
        if (categories.length > 0 && !selectedCategoryId) {
          selectedCategoryId = categories[0]._id;
          await fetchVouchers();
        } else if (categories.length === 0) {
          vouchers = [];
          loading = false;
        } else {
          await fetchVouchers();
        }
      } else {
        throw new Error(response.message || 'Failed to load categories');
      }
    } catch (err) {
      console.error('Error loading categories:', err);
      error = 'Failed to load voucher categories. Please try again.';
      showAlert('error', error);
      loading = false;
    }
  }
  
  // Load vouchers based on selected category
  async function fetchVouchers() {
    if (!selectedCategoryId) {
      loading = false;
      return;
    }
    
    try {
      loading = true;
      
      const params = new URLSearchParams({
        page,
        limit: 10
      });
      
      if (voucherStatus !== 'all') {
        params.append('status', voucherStatus);
      }
      
      const response = await authFetch(`/vouchers/${selectedCategoryId}?${params}`);
      
      if (response.success) {
        vouchers = response.vouchers;
        totalPages = response.totalPages;
        currentPage = response.currentPage;
        total = response.total;
      } else {
        throw new Error(response.message || 'Failed to load vouchers');
      }
    } catch (err) {
      console.error('Error loading vouchers:', err);
      error = 'Failed to load vouchers. Please try again.';
      showAlert('error', error);
    } finally {
      loading = false;
    }
  }
  
  // Handle title selection change
  function handleTitleChange() {
    selectedCategoryId = '';
    fetchCategories();
  }
  
  // Handle category selection change
  function handleCategoryChange() {
    page = 1;
    fetchVouchers();
  }
  
  // Handle status filter change
  function handleStatusChange() {
    page = 1;
    fetchVouchers();
  }
  
  // Handle pagination
  function goToPage(newPage) {
    if (newPage < 1 || newPage > totalPages) return;
    
    page = newPage;
    currentPage = newPage; // Update currentPage to sync with page
    fetchVouchers();
  }
  
  // Create new title
  async function createTitle() {
    if (!newTitleName.trim()) {
      showAlert('error', 'Nama judul voucher wajib diisi');
      return;
    }
    
    try {
      // Use the authFetch helper to handle API calls
      const data = await authFetch('/vouchers/titles', {
        method: 'POST',
        body: JSON.stringify({ name: newTitleName })
      });
      
      if (data.success) {
        showAlert('success', 'Judul voucher berhasil dibuat');
        newTitleName = '';
        showCreateTitleModal = false;
        await fetchTitles();
      } else {
        throw new Error(data.message || 'Failed to create title');
      }
    } catch (err) {
      console.error('Error creating title:', err);
      showAlert('error', 'Gagal membuat judul voucher');
    }
  }
  
  // Handle image selection
  function handleImageChange(event) {
    const file = event.target.files[0];
    
    if (file) {
      newCategory.image = file;
      
      // Create preview
      const reader = new FileReader();
      reader.onload = e => {
        imagePreview = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }
  
  // Create new category
  async function createCategory() {
    // Validate inputs
    if (!newCategory.titleId || !newCategory.nominal || !newCategory.amount || !newCategory.codePrefix || !newCategory.image) {
      showAlert('error', 'Semua field wajib diisi');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('titleId', newCategory.titleId);
      formData.append('nominal', newCategory.nominal);
      formData.append('amount', newCategory.amount);
      formData.append('codePrefix', newCategory.codePrefix);
      formData.append('posX', newCategory.posX);
      formData.append('posY', newCategory.posY);
      formData.append('qrSize', newCategory.qrSize || 100);
      formData.append('showCodeText', newCategory.showCodeText === false ? 'false' : 'true');
      formData.append('image', newCategory.image);
      
      const response = await fetch('/api/vouchers/categories', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      const data = await response.json();
      
      if (data.success) {
        showAlert('success', 'Kategori voucher berhasil dibuat');
        
        // Reset form
        newCategory = {
          titleId: selectedTitleId,
          nominal: '',
          amount: '',
          codePrefix: '',
          image: null,
          posX: 50,
          posY: 50,
          qrSize: 100,
          showCodeText: true
        };
        imagePreview = null;
        showCreateCategoryModal = false;
        
        // Refresh data
        await fetchCategories();
      } else {
        throw new Error(data.message || 'Failed to create category');
      }
    } catch (err) {
      console.error('Error creating category:', err);
      showAlert('error', 'Gagal membuat kategori voucher');
    }
  }
  
  // Delete title
  async function deleteTitle() {
    if (!titleToDelete) return;
    
    try {
      const response = await authFetch(`/vouchers/titles/${titleToDelete._id}`, {
        method: 'DELETE'
      });
      
      if (response.success) {
        showAlert('success', 'Judul voucher berhasil dihapus');
        titleToDelete = null;
        
        // Refresh data
        await fetchTitles();
      } else {
        throw new Error(response.message || 'Failed to delete title');
      }
    } catch (err) {
      console.error('Error deleting title:', err);
      showAlert('error', 'Gagal menghapus judul voucher');
    }
  }
  
  // Delete category
  async function deleteCategory() {
    if (!categoryToDelete) return;
    
    try {
      const response = await authFetch(`/vouchers/categories/${categoryToDelete._id}`, {
        method: 'DELETE'
      });
      
      if (response.success) {
        showAlert('success', 'Kategori voucher berhasil dihapus');
        categoryToDelete = null;
        
        // Refresh data
        await fetchCategories();
      } else {
        throw new Error(response.message || 'Failed to delete category');
      }
    } catch (err) {
      console.error('Error deleting category:', err);
      showAlert('error', 'Gagal menghapus kategori voucher');
    }
  }
  
  // Mark voucher as used
  async function markVoucherAsUsed(code) {
    try {
      const response = await authFetch(`/vouchers/mark-used/${code}`, {
        method: 'POST'
      });
      
      if (response.success) {
        showAlert('success', 'Voucher berhasil ditandai sebagai terpakai');
        
        // Refresh data
        await fetchVouchers();
      } else {
        throw new Error(response.message || 'Failed to mark voucher as used');
      }
    } catch (err) {
      console.error('Error marking voucher as used:', err);
      showAlert('error', err.message || 'Gagal menandai voucher sebagai terpakai');
    }
  }
  
  // Mark voucher as unused (admin only)
  async function handleMarkVoucherAsUnused(code) {
    try {
      const response = await markVoucherAsUnused(code);
      
      if (response.success) {
        showAlert('success', 'Voucher berhasil dikembalikan (belum terpakai)');
        
        // Refresh data
        await fetchVouchers();
      } else {
        throw new Error(response.message || 'Failed to mark voucher as unused');
      }
    } catch (err) {
      console.error('Error marking voucher as unused:', err);
      showAlert('error', err.message || 'Gagal mengembalikan voucher');
    }
  }
  
  async function exportQrCode(code) {
    try {
      // Show loading indicator
      showAlert('info', 'Mengunduh QR Code...');
      
      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Create a direct link with authorization header for download
      const link = document.createElement('a');
      
      // Using fetch directly for blob handling
      const response = await fetch(`/api/export/qrcode/${code}`, {
        method: 'GET',
        headers: {
          'Accept': 'image/png',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('token');
          navigate('/login');
          throw new Error('Sesi anda telah berakhir. Silakan login kembali.');
        }
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      // Convert the response to a blob
      const blob = await response.blob();
      
      if (blob.size === 0) {
        throw new Error('QR Code tidak dapat dihasilkan. File kosong.');
      }
      
      // Create a URL for the blob
      const url = window.URL.createObjectURL(blob);
      
      // Set up download link
      link.href = url;
      link.download = `voucher-${code}.png`;
      
      // Append to document body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Revoke the blob URL to free memory
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
      
      showAlert('success', 'QR Code berhasil diexport');
    } catch (err) {
      console.error('Error exporting QR code:', err);
      showAlert('error', 'Gagal mengexport QR Code: ' + (err.message || 'Unknown error'));
    }
  }
  
  // Download voucher with QR code (admin only)
  async function handleDownloadVoucher(code) {
    try {
      // Show loading indicator
      showAlert('info', 'Mengunduh Voucher...');
      
      // Call the API function
      await downloadVoucherWithQR(code);
      
      showAlert('success', 'Voucher berhasil diunduh');
    } catch (err) {
      console.error('Error downloading voucher:', err);
      showAlert('error', 'Gagal mengunduh voucher: ' + (err.message || 'Unknown error'));
    }
  }
  
  // Format date
  function formatDate(dateString) {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }
  
  // Open create category modal with pre-selected title
  function openCreateCategoryModal() {
    newCategory.titleId = selectedTitleId;
    showCreateCategoryModal = true;
  }
  
  // Load data on mount
  onMount(() => {
    fetchTitles();
  });
</script>

<div class="vouchers-container">
  <div class="page-header">
    <h1>Manajemen Voucher</h1>
    
    <div class="header-actions">
      <button class="add-title-button" on:click={() => showCreateTitleModal = true}>
        + Tambah Judul
      </button>
      <button class="add-category-button" on:click={openCreateCategoryModal}>
        + Tambah Kategori
      </button>
    </div>
  </div>
  
  <div class="vouchers-filters">
    <div class="filter-item">
      <label for="title-select">Judul Voucher:</label>
      <select id="title-select" bind:value={selectedTitleId} on:change={handleTitleChange}>
        <option value="0">Semua Judul</option>
        {#each titles as title}
          <option value={title._id}>{title.name}</option>
        {/each}
      </select>
    </div>
    
    {#if selectedTitleId !== '0'}
      <div class="filter-item">
        <label for="category-select">Kategori:</label>
        <select id="category-select" bind:value={selectedCategoryId} on:change={handleCategoryChange}>
          <option value="">Pilih Kategori</option>
          {#each categories as category}
            <option value={category._id}>
              {category.titleId.name} - Rp {category.nominal.toLocaleString('id-ID')}
            </option>
          {/each}
        </select>
      </div>
    {/if}
    
    {#if selectedCategoryId}
      <div class="filter-item">
        <label for="status-select">Status:</label>
        <select id="status-select" bind:value={voucherStatus} on:change={handleStatusChange}>
          <option value="all">Semua</option>
          <option value="used">Terpakai</option>
          <option value="unused">Belum Terpakai</option>
        </select>
      </div>
    {/if}
  </div>
  
  {#if loading}
    <div class="loading-container">
      <div class="loading-spinner"></div>
      <p>Loading data...</p>
    </div>
  {:else if error}
    <div class="error-container">
      <p>{error}</p>
      <button on:click={fetchTitles}>Coba Lagi</button>
    </div>
  {:else}
    <!-- Title list -->
    {#if selectedTitleId === '0'}
      <div class="section-header">
        <h2>Daftar Judul Voucher</h2>
      </div>
      
      {#if titles.length === 0}
        <div class="empty-state">
          <p>Belum ada judul voucher. Silakan tambahkan judul baru.</p>
        </div>
      {:else}
        <div class="titles-grid">
          {#each titles as title}
            <div class="title-card">
              <div class="title-content">
                <h3>{title.name}</h3>
                <p>
                  {#if title.categories && title.categories.length}
                    {title.categories.length} kategori
                  {:else}
                    Belum ada kategori
                  {/if}
                </p>
              </div>
              <div class="title-actions">
                <button class="delete-button" on:click={() => titleToDelete = title}>
                  Hapus
                </button>
              </div>
            </div>
          {/each}
        </div>
      {/if}
    
    <!-- Category list -->
    {:else if !selectedCategoryId}
      <div class="section-header">
        <h2>Daftar Kategori Voucher</h2>
      </div>
      
      {#if categories.length === 0}
        <div class="empty-state">
          <p>Belum ada kategori untuk judul ini. Silakan tambahkan kategori baru.</p>
        </div>
      {:else}
        <div class="categories-grid">
          {#each categories as category}
            <div class="category-card" on:click={() => { selectedCategoryId = category._id; handleCategoryChange(); }}>
              <div class="category-image">
                <img src={category.imagePath} alt={`${category.titleId.name} - ${category.nominal}`} />
              </div>
              <div class="category-content">
                <h3>{category.titleId.name}</h3>
                <p class="category-nominal">Rp {category.nominal.toLocaleString('id-ID')}</p>
                <div class="category-stats">
                  <div class="stat-item">
                    <span class="stat-label">Total:</span>
                    <span class="stat-value">{category.voucherCount}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Terpakai:</span>
                    <span class="stat-value">{category.usedCount}</span>
                  </div>
                  <div class="stat-item">
                    <span class="stat-label">Sisa:</span>
                    <span class="stat-value">{category.voucherCount - category.usedCount}</span>
                  </div>
                </div>
              </div>
              <div class="category-actions">
                <button class="delete-button" on:click|stopPropagation={() => categoryToDelete = category}>
                  Hapus
                </button>
              </div>
            </div>
          {/each}
        </div>
      {/if}
    
    <!-- Voucher list -->
    {:else}
      <div class="section-header">
        <h2>
          Daftar Voucher
          {#if selectedCategoryId && categories.length > 0}
            {#each categories as category}
              {#if category._id === selectedCategoryId}
                - {category.titleId.name} Rp {category.nominal.toLocaleString('id-ID')}
              {/if}
            {/each}
          {/if}
        </h2>
        <div class="vouchers-status-count">
          <span>Menampilkan {vouchers.length} dari {total} voucher</span>
        </div>
      </div>
      
      {#if vouchers.length === 0}
        <div class="empty-state">
          <p>
            {#if voucherStatus === 'used'}
              Belum ada voucher yang terpakai.
            {:else if voucherStatus === 'unused'}
              Semua voucher sudah terpakai.
            {:else}
              Belum ada voucher untuk kategori ini.
            {/if}
          </p>
        </div>
      {:else}
        <div class="vouchers-table-container">
          <table class="vouchers-table">
            <thead>
              <tr>
                <th>Kode</th>
                <th>Status</th>
                <th>Tanggal Digunakan</th>
                <th>Pendaftar</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {#each vouchers as voucher}
                <tr class={voucher.used ? 'used' : ''}>
                  <td>{voucher.code}</td>
                  <td>
                    <span class={`status-badge ${voucher.used ? 'used' : 'unused'}`}>
                      {voucher.used ? 'Terpakai' : 'Belum Terpakai'}
                    </span>
                  </td>
                  <td>{formatDate(voucher.usedAt)}</td>
                  <td>
                    {#if voucher.registrarName || voucher.registrarUsername}
                      {voucher.registrarName || voucher.registrarUsername}
                    {:else if voucher.registrarId && (voucher.registrarId.name || voucher.registrarId.username)}
                      {voucher.registrarId.name || voucher.registrarId.username}
                    {:else}
                      -
                    {/if}
                  </td>
                  <td class="actions-cell">
                    <button 
                      class="export-button" 
                      on:click={() => exportQrCode(voucher.code)}
                      title="Export QR Code"
                    >
                      <span class="icon">🔄</span>QR
                    </button>
                    
                    {#if $user && $user.role === 'admin'}
                      <button 
                        class="download-button" 
                        on:click={() => handleDownloadVoucher(voucher.code)}
                        title="Download Voucher dengan QR"
                      >
                        <span class="icon">📄</span>Download
                      </button>
                    {/if}
                    
                    {#if !voucher.used}
                      <button 
                        class="use-button" 
                        on:click={() => markVoucherAsUsed(voucher.code)}
                      >
                        Tandai Terpakai
                      </button>
                    {:else if $user && $user.role === 'admin'}
                      <button 
                        class="undo-button" 
                        on:click={() => handleMarkVoucherAsUnused(voucher.code)}
                      >
                        Kembalikan Voucher
                      </button>
                    {/if}
                  </td>
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
        
        {#if totalPages > 1}
          <div class="pagination">
            <button 
              class="page-button" 
              disabled={currentPage === 1}
              on:click={() => goToPage(currentPage - 1)}
            >
              &laquo; Prev
            </button>
            
            {#if totalPages <= 10}
              {#each Array(totalPages) as _, i}
                <button 
                  class="page-number" 
                  class:active={currentPage === i + 1}
                  on:click={() => goToPage(i + 1)}
                >
                  {i + 1}
                </button>
              {/each}
            {:else}
              <!-- Show first page -->
              {#if currentPage > 4}
                <button 
                  class="page-number"
                  on:click={() => goToPage(1)}
                >
                  1
                </button>
                
                <!-- Show ellipsis if we're not on the first few pages -->
                {#if currentPage > 5}
                  <span class="pagination-ellipsis">...</span>
                {/if}
              {/if}
              
              <!-- Show 2 pages before and 2 pages after current page -->
              {#each Array(5).fill().map((_, idx) => currentPage - 2 + idx).filter(p => p > 0 && p <= totalPages) as pageNum}
                <button 
                  class="page-number" 
                  class:active={currentPage === pageNum}
                  on:click={() => goToPage(pageNum)}
                >
                  {pageNum}
                </button>
              {/each}
              
              <!-- Show ellipsis if we're not on the last few pages -->
              {#if currentPage < totalPages - 4}
                <span class="pagination-ellipsis">...</span>
                
                <!-- Show last page -->
                <button 
                  class="page-number"
                  on:click={() => goToPage(totalPages)}
                >
                  {totalPages}
                </button>
              {/if}
            {/if}
            
            <button 
              class="page-button" 
              disabled={currentPage === totalPages}
              on:click={() => goToPage(currentPage + 1)}
            >
              Next &raquo;
            </button>
          </div>
        {/if}
      {/if}
    {/if}
  {/if}
</div>

<!-- Create Title Modal -->
{#if showCreateTitleModal}
  <div class="modal-backdrop" on:click={() => showCreateTitleModal = false}>
    <div class="modal-content" on:click|stopPropagation>
      <h2>Tambah Judul Voucher</h2>
      
      <div class="form-group">
        <label for="title-name">Nama Judul</label>
        <input 
          type="text" 
          id="title-name" 
          bind:value={newTitleName} 
          placeholder="Masukkan nama judul voucher"
        />
      </div>
      
      <div class="modal-actions">
        <button class="cancel-button" on:click={() => showCreateTitleModal = false}>
          Batal
        </button>
        <button class="save-button" on:click={createTitle}>
          Simpan
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Create Category Modal -->
{#if showCreateCategoryModal}
  <div class="modal-backdrop" on:click={() => showCreateCategoryModal = false}>
    <div class="modal-content category-modal" on:click|stopPropagation>
      <h2>Tambah Kategori Voucher</h2>
      
      <div class="form-group">
        <label for="category-title">Judul Voucher</label>
        <select id="category-title" bind:value={newCategory.titleId}>
          <option value="">Pilih Judul</option>
          {#each titles as title}
            <option value={title._id}>{title.name}</option>
          {/each}
        </select>
      </div>
      
      <div class="form-group">
        <label for="category-nominal">Nominal (Rp)</label>
        <input 
          type="number" 
          id="category-nominal" 
          bind:value={newCategory.nominal} 
          placeholder="Masukkan nominal voucher"
        />
      </div>
      
      <div class="form-group">
        <label for="category-amount">Jumlah Voucher</label>
        <input 
          type="number" 
          id="category-amount" 
          bind:value={newCategory.amount} 
          placeholder="Masukkan jumlah voucher"
        />
      </div>
      
      <div class="form-group">
        <label for="category-prefix">Prefix Kode</label>
        <input 
          type="text" 
          id="category-prefix" 
          bind:value={newCategory.codePrefix} 
          placeholder="Contoh: VC-"
        />
      </div>
      
      <div class="form-group">
        <label for="category-image">Gambar Voucher</label>
        <input 
          type="file" 
          id="category-image" 
          accept="image/*"
          on:change={handleImageChange}
        />
        
        {#if imagePreview}
          <div class="image-preview">
            <img src={imagePreview} alt="Preview" />
          </div>
        {/if}
      </div>
      
      <h3 class="form-section-title">Posisi QR Code</h3>
      <div class="form-row">
        <div class="form-group">
          <label for="category-posX">Posisi X QR Code (%)</label>
          <input 
            type="number" 
            id="category-posX" 
            bind:value={newCategory.posX} 
            min="0"
            max="100"
          />
        </div>
        
        <div class="form-group">
          <label for="category-posY">Posisi Y QR Code (%)</label>
          <input 
            type="number" 
            id="category-posY" 
            bind:value={newCategory.posY}
            min="0"
            max="100"
          />
        </div>
      </div>
      
      <h3 class="form-section-title">Pengaturan QR Code</h3>
      <div class="form-row">
        <div class="form-group">
          <label for="category-qrSize">Ukuran QR Code (px)</label>
          <input 
            type="number" 
            id="category-qrSize" 
            bind:value={newCategory.qrSize}
            min="50"
            max="300"
            placeholder="Default: 100px"
          />
          <div class="form-help">Ukuran default: 100x100px</div>
        </div>
        
        <div class="form-group">
          <label for="category-showCodeText">Tampilkan Teks Kode Voucher</label>
          <select id="category-showCodeText" bind:value={newCategory.showCodeText}>
            <option value={true}>Ya, tampilkan teks</option>
            <option value={false}>Tidak, QR code saja</option>
          </select>
        </div>
      </div>
      
      <div class="modal-actions">
        <button class="cancel-button" on:click={() => showCreateCategoryModal = false}>
          Batal
        </button>
        <button class="save-button" on:click={createCategory}>
          Simpan
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Delete Title Confirmation Modal -->
{#if titleToDelete}
  <div class="modal-backdrop" on:click={() => titleToDelete = null}>
    <div class="modal-content confirmation-modal" on:click|stopPropagation>
      <h2>Konfirmasi Hapus</h2>
      <p>Apakah Anda yakin ingin menghapus judul voucher "{titleToDelete.name}"?</p>
      <p class="warning-text">Perhatian: Semua kategori dan voucher di bawah judul ini juga akan dihapus.</p>
      
      <div class="modal-actions">
        <button class="cancel-button" on:click={() => titleToDelete = null}>
          Batal
        </button>
        <button class="delete-confirm-button" on:click={deleteTitle}>
          Hapus
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Delete Category Confirmation Modal -->
{#if categoryToDelete}
  <div class="modal-backdrop" on:click={() => categoryToDelete = null}>
    <div class="modal-content confirmation-modal" on:click|stopPropagation>
      <h2>Konfirmasi Hapus</h2>
      <p>Apakah Anda yakin ingin menghapus kategori voucher ini?</p>
      <p class="warning-text">Perhatian: Semua voucher di bawah kategori ini juga akan dihapus.</p>
      
      <div class="modal-actions">
        <button class="cancel-button" on:click={() => categoryToDelete = null}>
          Batal
        </button>
        <button class="delete-confirm-button" on:click={deleteCategory}>
          Hapus
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .vouchers-container {
    padding: 0;
  }
  
  .page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .page-header h1 {
    font-size: 1.75rem;
    font-weight: 700;
    color: #1e3a8a;
    margin: 0;
    letter-spacing: -0.025em;
  }
  
  .header-actions {
    display: flex;
    gap: 0.5rem;
  }
  
  .add-title-button, .add-category-button {
    padding: 0.5rem 1rem;
    border-radius: 0.375rem;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .add-title-button {
    background-color: #3b82f6;
    color: white;
    border: none;
  }
  
  .add-title-button:hover {
    background-color: #2563eb;
  }
  
  .add-category-button {
    background-color: #10b981;
    color: white;
    border: none;
  }
  
  .add-category-button:hover {
    background-color: #059669;
  }
  
  .vouchers-filters {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.5rem;
    padding: 1rem;
    background-color: white;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  .filter-item {
    display: flex;
    flex-direction: column;
    min-width: 200px;
  }
  
  .filter-item label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #4b5563;
    margin-bottom: 0.25rem;
  }
  
  .filter-item select {
    padding: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    background-color: white;
    font-size: 0.875rem;
    color: #1f2937;
  }
  
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 5rem 0;
    background-color: white;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }
  
  .loading-spinner {
    width: 3rem;
    height: 3rem;
    border: 4px solid #e5e7eb;
    border-top: 4px solid #3b82f6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 1.25rem;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2.5rem;
    background-color: #fee2e2;
    border-radius: 0.5rem;
    color: #b91c1c;
    text-align: center;
  }
  
  .error-container button {
    margin-top: 1rem;
    padding: 0.5rem 1.25rem;
    background-color: #dc2626;
    color: white;
    border: none;
    border-radius: 0.375rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
  }
  
  .error-container button:hover {
    background-color: #b91c1c;
  }
  
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }
  
  .section-header h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e40af;
    margin: 0;
  }
  
  .empty-state {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 3rem;
    background-color: white;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    color: #6b7280;
  }
  
  /* Title cards */
  .titles-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
  }
  
  .title-card {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem;
    background-color: white;
    border-radius: 0.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s, box-shadow 0.2s;
  }
  
  .title-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
  }
  
  .title-content h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #1e3a8a;
    margin: 0 0 0.5rem 0;
  }
  
  .title-content p {
    margin: 0;
    font-size: 0.875rem;
    color: #6b7280;
  }
  
  /* Category cards */
  .categories-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 1.25rem;
  }
  
  .category-card {
    display: flex;
    background-color: white;
    border-radius: 0.5rem;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    position: relative;
  }
  
  .category-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);
  }
  
  .category-image {
    width: 100px;
    min-width: 100px;
    overflow: hidden;
  }
  
  .category-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  
  .category-content {
    flex: 1;
    padding: 1rem;
  }
  
  .category-content h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #1e3a8a;
    margin: 0 0 0.25rem 0;
  }
  
  .category-nominal {
    font-size: 1.25rem;
    font-weight: 700;
    color: #10b981;
    margin: 0 0 0.75rem 0;
  }
  
  .category-stats {
    display: flex;
    gap: 1rem;
  }
  
  .stat-item {
    display: flex;
    flex-direction: column;
    font-size: 0.75rem;
  }
  
  .stat-label {
    color: #6b7280;
  }
  
  .stat-value {
    font-weight: 600;
    color: #1f2937;
  }
  
  /* Vouchers table */
  .vouchers-status-count {
    font-size: 0.875rem;
    color: #6b7280;
  }
  
  .vouchers-table-container {
    background-color: white;
    border-radius: 0.5rem;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    margin-bottom: 1rem;
  }
  
  .vouchers-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;
  }
  
  .vouchers-table th {
    text-align: left;
    padding: 0.75rem 1rem;
    background-color: #f8fafc;
    color: #4b5563;
    font-weight: 600;
    border-bottom: 1px solid #e2e8f0;
  }
  
  .vouchers-table td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #e2e8f0;
    color: #1f2937;
  }
  
  .vouchers-table tr:last-child td {
    border-bottom: none;
  }
  
  .vouchers-table tr.used td {
    background-color: #f9fafb;
    color: #6b7280;
  }
  
  .status-badge {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 500;
  }
  
  .status-badge.used {
    background-color: #dcfce7;
    color: #16a34a;
  }
  
  .status-badge.unused {
    background-color: #ffedd5;
    color: #ea580c;
  }
  
  .use-button {
    padding: 0.375rem 0.75rem;
    background-color: #3b82f6;
    color: white;
    border: none;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
  }
  
  .use-button:hover {
    background-color: #2563eb;
  }
  
  .export-button {
    padding: 0.375rem 0.75rem;
    background-color: #10b981;
    color: white;
    border: none;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    margin-right: 0.5rem;
  }
  
  .export-button:hover {
    background-color: #059669;
  }
  
  .download-button {
    padding: 0.375rem 0.75rem;
    background-color: #8b5cf6;
    color: white;
    border: none;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    margin-right: 0.5rem;
  }
  
  .download-button:hover {
    background-color: #7c3aed;
  }
  
  .undo-button {
    padding: 0.375rem 0.75rem;
    background-color: #f59e0b;
    color: white;
    border: none;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
  }
  
  .undo-button:hover {
    background-color: #d97706;
  }
  
  .actions-cell {
    display: flex;
    gap: 0.5rem;
  }
  
  .icon {
    margin-right: 0.25rem;
  }
  
  .delete-button {
    padding: 0.375rem 0.75rem;
    background-color: #ef4444;
    color: white;
    border: none;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
  }
  
  .delete-button:hover {
    background-color: #dc2626;
  }
  
  /* Pagination */
  .pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.25rem;
    margin-top: 1.5rem;
  }
  
  .page-button, .page-number {
    padding: 0.5rem 0.75rem;
    background-color: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    cursor: pointer;
    color: #4b5563;
    font-size: 0.875rem;
    transition: all 0.2s;
  }
  
  .page-button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
  
  .page-button:not(:disabled):hover, .page-number:hover {
    background-color: #f3f4f6;
  }
  
  .page-number.active {
    background-color: #3b82f6;
    color: white;
    border-color: #3b82f6;
  }
  
  /* Modals */
  .modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .modal-content {
    background-color: white;
    border-radius: 0.5rem;
    padding: 1.5rem;
    width: 100%;
    max-width: 500px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1);
  }
  
  .modal-content h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #1e3a8a;
    margin: 0 0 1.5rem 0;
  }
  
  .category-modal {
    max-width: 600px;
    max-height: 90vh;
    overflow-y: auto;
  }
  
  .form-group {
    margin-bottom: 1.25rem;
  }
  
  .form-row {
    display: flex;
    gap: 1rem;
  }
  
  .form-row .form-group {
    flex: 1;
  }
  
  .form-group label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: #4b5563;
    margin-bottom: 0.5rem;
  }
  
  .form-group input, .form-group select {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.875rem;
    color: #1f2937;
  }
  
  .form-help {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 0.25rem;
  }
  
  .form-section-title {
    font-size: 1rem;
    font-weight: 600;
    color: #4b5563;
    margin: 1.5rem 0 0.75rem 0;
    border-bottom: 1px solid #e5e7eb;
    padding-bottom: 0.5rem;
  }
  
  .image-preview {
    margin-top: 0.5rem;
    width: 100%;
    max-height: 200px;
    overflow: hidden;
    border-radius: 0.25rem;
    border: 1px solid #e5e7eb;
  }
  
  .image-preview img {
    width: 100%;
    height: auto;
    display: block;
  }
  
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1.5rem;
  }
  
  .save-button, .cancel-button, .delete-confirm-button {
    padding: 0.5rem 1rem;
    border-radius: 0.375rem;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s;
  }
  
  .save-button {
    background-color: #3b82f6;
    color: white;
    border: none;
  }
  
  .save-button:hover {
    background-color: #2563eb;
  }
  
  .cancel-button {
    background-color: #f3f4f6;
    color: #4b5563;
    border: 1px solid #d1d5db;
  }
  
  .cancel-button:hover {
    background-color: #e5e7eb;
  }
  
  .delete-confirm-button {
    background-color: #ef4444;
    color: white;
    border: none;
  }
  
  .delete-confirm-button:hover {
    background-color: #dc2626;
  }
  
  .confirmation-modal {
    max-width: 400px;
  }
  
  .warning-text {
    color: #b91c1c;
    font-size: 0.875rem;
    margin-top: 0.5rem;
  }
  
  /* Pagination ellipsis style */
  .pagination-ellipsis {
    display: inline-block;
    padding: 0.375rem 0.75rem;
    color: #6b7280;
  }
  
  @media (max-width: 768px) {
    .titles-grid, .categories-grid {
      grid-template-columns: 1fr;
    }
    
    .category-card {
      flex-direction: column;
    }
    
    .category-image {
      width: 100%;
      height: 150px;
    }
    
    .form-row {
      flex-direction: column;
      gap: 0.5rem;
    }
  }
</style>